import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowRight, Sparkles } from 'lucide-react'
import PageHero from '../shared/PageHero'
import { contentApi, ServiceItem } from '../../services/contentApi'
import { getImageUrl, IMAGE_CONFIG } from '../../config/settings'

const fallbackImages: Record<string, string> = {
  'home-stays': 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=800&q=80',
  rooms: 'https://images.unsplash.com/photo-1631049307264-da0ec9d70304?w=800&q=80',
  cabs: 'https://images.unsplash.com/photo-1449824913935-59a10b8d2000?w=800&q=80',
}

const ServicesPage = () => {
  const [services, setServices] = useState<ServiceItem[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    contentApi.getServices()
      .then((res) => setServices(res.data))
      .catch(() => setServices([]))
      .finally(() => setLoading(false))
  }, [])

  return (
    <div className="min-h-screen bg-[#fafafa]">
      <PageHero
        eyebrow="Aonla Stay Services"
        title="Everything you need for your Manauna yatra"
        subtitle="Stays, transport, events, and local services — curated for pilgrims visiting Shri Khatu Shyam Ji Mandir near Aonla."
      />

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="h-72 rounded-2xl bg-gray-200 animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {services.map((service) => {
              const img = service.icon ? getImageUrl(service.icon) : fallbackImages[String(service.slug)] || IMAGE_CONFIG.FALLBACK_IMAGE
              return (
                <Link
                  key={String(service.id)}
                  to={`/services/${service.slug || service.id}`}
                  className="group relative overflow-hidden rounded-2xl bg-white border border-gray-200/80 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
                >
                  <div className="aspect-[16/10] overflow-hidden">
                    <img src={img} alt={service.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
                    {service.is_featured && (
                      <span className="absolute top-4 left-4 inline-flex items-center gap-1 rounded-full bg-white/95 px-3 py-1 text-xs font-semibold text-secondary-700">
                        <Sparkles className="h-3.5 w-3.5 text-primary-500" /> Featured
                      </span>
                    )}
                  </div>
                  <div className="p-5 sm:p-6">
                    <h2 className="text-xl font-bold text-gray-900 group-hover:text-primary-700 transition-colors">{service.title}</h2>
                    <p className="mt-2 text-sm text-gray-600 line-clamp-2 leading-relaxed">{service.summary}</p>
                    <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-primary-600">
                      Explore <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
                    </span>
                  </div>
                </Link>
              )
            })}
          </div>
        )}
      </section>
    </div>
  )
}

export default ServicesPage
